import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { EmailContract, EmailUpdateRequest } from '@app-types/api/email';
import {
  BaseGetByEmailFileIdRequest,
  BaseGetByIdRequest,
} from '@app-types/base/base';
import { BaseCollectionFilteredSearchByIdRequest } from '@app-types/common';
import { defaultFilter } from '@app-shared/constants/constants';
import { ContactsSearchOrder } from '@app-types/enums/contacts';
import { EmailTagClient } from '@app-services/api/clients/email-tag.client';
import { PermissionType } from '@app-types/enums/permission-type';
import { PermissionService } from '@app-services/permission/permission.service';
import { environment } from '@app-environments/environment';
import { filter } from 'rxjs/operators';
import objectHash from 'object-hash';
import { EmailBodyContentType } from '@app-types/enums/email-body-content-type';
import { EmailState } from '@app-types/enums/email-state';
import { EmailClient } from '@app-services/api/clients/email-client';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { FileClient } from '@app-services/api/clients/file.client';
import { MatDialog } from '@angular/material/dialog';
import { EmailContactsDialogComponent } from '../email-contacts-dialog/email-contacts-dialog.component';
import { ContactClient } from '@app-services/api/clients/contact.client';
import { ErrorDialogComponent } from '../../common/error-dialog/error-dialog.component';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TemplateGetByIdContract } from '@app-types/api/template';
import { AttachmentState } from 'src/app/types/enums/attachment-state';
import { TranslateModule } from '@ngx-translate/core';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { LoadingButtonComponent } from '../../common/loading-button/loading-button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { EmailTagsComponent } from '../../common/email/email-tags/email-tags.component';
import { MatInputModule } from '@angular/material/input';
import { EMAIL_REGEX } from '@app-shared/constants/regex';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DrawerService } from '@app-services/drawer.service';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { EmailMenuContent } from '@app-types/enums/emails';
import { EmailBodyComponent } from '../../common/email/email-body/email-body.component';
import { EmailSidenavComponent } from '../../common/email/email-sidenav/email-sidenav.component';
import { SkeletonEmailComponent } from '@app-components/common/skeletons/skeleton-email/skeleton-email.component';
import { ErrorsPipe } from '@app-pipes/error-code.pipe';
import {
  Attachment,
  AttachmentUploadContract,
} from '@app-types/api/attachment';
import {
  excludeInlineAttachments,
  prepareEmailBodyHtml,
  sanitizeEmailBodyHtml,
} from '@app-shared/utils/email-body';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { ChangedTags, TagContract } from '@app-types/api/tag';
import { EmailAttachmentsListComponent } from '@app-components/email/email-attachments-list/email-attachments-list.component';
import { AuditButtonComponent } from '@app-components/email/action-buttons/audit-button/audit-button.component';
import { DeleteButtonComponent } from '@app-components/email/action-buttons/delete-button/delete-button.component';
import { NotesButtonComponent } from '@app-components/email/action-buttons/notes-button/notes-button.component';
import { SnackbarService } from '@app-services/snackbar.service';
import { FieldTagsListComponent } from '@app-components/common/field-tags-list/field-tags-list.component';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';

@Component({
  selector: 'app-email-create-or-update',
  templateUrl: './email-create-or-update.component.html',
  styleUrls: ['./email-create-or-update.component.scss'],
  imports: [
    SomethingWentWrongComponent,
    LoadingButtonComponent,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgStyle,
    MatSlideToggleModule,
    TranslateModule,
    EmailTagsComponent,
    MatInputModule,
    MatTooltipModule,
    EmailBodyComponent,
    EmailSidenavComponent,
    SkeletonEmailComponent,
    TourAnchorMatMenuDirective,
    EmailAttachmentsListComponent,
    NgTemplateOutlet,
    AuditButtonComponent,
    DeleteButtonComponent,
    NotesButtonComponent,
    FieldTagsListComponent,
  ],
})
export class EmailCreateOrUpdateComponent implements OnInit, OnDestroy {
  @Input() public mailAccountId: number;
  @Input() public emailId?: number;
  /** Original email id for resolving cid: images (e.g. reply draft without copied attachments). */
  @Input() public inlineImagesSourceEmailId?: number;
  @Input() public initialToRecipients: string[] = [];
  @Input() public useMobileView: boolean;
  @Output() public submitted = new EventEmitter();
  @Output() public refreshNotesDraft = new EventEmitter();
  @Output() public deleteFromData = new EventEmitter<any>();
  @Output() public changeDraftData = new EventEmitter<any>();
  @ViewChild('uploader') uploader: ElementRef;
  public email: EmailContract;
  public isLoading = true;
  public isContactsLoadingTo = false;
  public isContactsLoadingCc = false;
  public cannotLoadData = false;
  public attachmentState = AttachmentState;
  public form: UntypedFormGroup;
  public senderAddress: string;
  public toList: string[] = [];
  public ccList: string[] = [];
  public initToList: string[] = [];
  public initCcList: string[] = [];
  public attachmentFiles: AttachmentUploadContract[] = [];
  public canSend = false;
  public canValidate = false;
  public thisTags: TagContract[] = [];
  public menuIsOpened = false;
  public menuContent: EmailMenuContent | null;
  private autoSaveSubscription: Subscription;
  public previousHash: string;
  public body = '';
  public totalSize = 0;
  public templateSubject: string;
  public templateBody: string;
  public showCc = false;
  public submitInProgress = false;
  public saveInProgress = false;
  public cannotSaveData = false;
  public contactsListTo: string[] = [];
  public contactsListCc: string[] = [];
  public initToContacts: string[] = [];
  public initCcContacts: string[] = [];
  public contactCtrl = new UntypedFormControl();
  public isNotesChanged = false;
  public isSplitEmail = false;
  public isSaveOnClose = true;
  public isNeedToUpdate = true;
  public newTags: TagContract[] = [];
  public currentTagsElement: number | null = null;
  pipeErrors = new ErrorsPipe();
  isDragging = false;

  constructor(
    private emailTagClient: EmailTagClient,
    private permissionService: PermissionService,
    private emailClient: EmailClient,
    public dialog: MatDialog,
    public matchError: MatchError,
    public contactClient: ContactClient,
    private fileClient: FileClient,
    private drawerService: DrawerService,
    public userWorkspaceService: UserWorkspaceService,
    private snackbarService: SnackbarService,
    public emailsPageStateService: EmailsPageStateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.canSend = await this.permissionService.hasPermissionOver(
      null,
      this.mailAccountId,
      PermissionType.CanSendEmail
    );
    this.canValidate = await this.permissionService.hasPermissionOver(
      null,
      this.mailAccountId,
      PermissionType.CanValidateEmail
    );
    await this.init();
  }

  async init(): Promise<void> {
    try {
      await this.loadData();
      if (this.email?.cc?.length) {
        this.showCc = true;
      }
      if (this.useMobileView) {
        this.showCc = true;
      }
      this.isSplitEmail = !!this.email?.splitSend;
      this.form = new UntypedFormGroup({
        to: new UntypedFormControl(''),
        cc: new UntypedFormControl(''),
        bcc: new UntypedFormControl(''),
        subject: new UntypedFormControl(this.email?.subject ?? '', [
          Validators.maxLength(2048),
        ]),
      });

      this.previousHash = this.calculateDraftHash();
      this.autoSaveSubscription = interval(environment.autoSaveIntervalMs)
        .pipe(
          filter(() => {
            if (this.isLoading || this.cannotLoadData) {
              return false;
            }
            const newHash = this.calculateDraftHash();
            const hashesAreNotEqual = this.previousHash !== newHash;
            if (hashesAreNotEqual) {
              this.previousHash = newHash;
            }
            return hashesAreNotEqual;
          })
        )
        .subscribe(() => {
          this.updateDraft(true).then();
        });
    } catch (e) {
      this.matchError.logError(e);
    }
  }

  async ngOnDestroy(): Promise<void> {
    if (this.isSaveOnClose && !this.cannotSaveData) {
      this.saveInProgress = true;
      await this.updateDraft(true);
      this.saveInProgress = false;
      this.submitted.emit();
    }
    this.isNeedToUpdate = false;
    this.autoSaveSubscription?.unsubscribe();
    this.emailsPageStateService.infoInitialContactsTo = null;
  }

  valueChangedEventEmitter(event: string): void {
    this.body = event;
    this.totalSize = new Blob([event]).size;
  }

  setCurrentTagsElement(value: number | null = null): void {
    if (value) {
      this.currentTagsElement = value;
      return;
    }
    const activeEle = document?.activeElement?.parentElement?.parentElement;
    const tagsTags = document.getElementById('tags-tags');
    const tagsCc = document.getElementById('tags-cc');
    const tagsTo = document.getElementById('tags-to');
    if (!activeEle) return;
    if (tagsTo?.contains(activeEle)) {
      this.currentTagsElement = 1;
    } else if (tagsCc?.contains(activeEle)) {
      this.currentTagsElement = 2;
    } else if (tagsTags?.contains(activeEle)) {
      this.currentTagsElement = 3;
    } else {
      this.currentTagsElement = null;
    }
  }

  private contactListRequest(
    search: string | null
  ): BaseCollectionFilteredSearchByIdRequest<ContactsSearchOrder> {
    return {
      mailAccountId: this.email.mailAccountId,
      offset: 0,
      fetch: 50,
      filter: { ...defaultFilter },
      search,
    };
  }

  public async getContactsTo(value: string): Promise<any> {
    this.isContactsLoadingTo = true;
    try {
      const search = value?.trim() ? value.trim() : null;
      const request = this.contactListRequest(search);
      const response = await this.contactClient.searchForAccount(request);
      this.contactsListTo = response.data.map(
        e => e.emails.find(el => el.isDefault)?.email ?? e.emails[0].email
      );
    } catch (e) {
      this.matchError.logError(e);
    } finally {
      this.isContactsLoadingTo = false;
    }
  }

  public async getContactsCc(value: string): Promise<any> {
    this.isContactsLoadingCc = true;
    try {
      const search = value?.trim() ? value.trim() : null;
      const request = this.contactListRequest(search);
      const response = await this.contactClient.searchForAccount(request);
      this.contactsListCc = response.data.map(
        e => e.emails.find(el => el.isDefault)?.email ?? e.emails[0].email
      );
    } catch (e) {
      this.matchError.logError(e);
    } finally {
      this.isContactsLoadingCc = false;
    }
  }

  public splitEmailToggle(): void {
    this.isSplitEmail = !this.isSplitEmail;
  }

  public openErrorDialog(
    title: string,
    description: string,
    subTitle?: string
  ): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '300px',
      autoFocus: false,
      data: { title, description, subTitle },
    });
  }

  public openContactsDialog(isCc = false): void {
    const dialogRef = this.dialog.open(EmailContactsDialogComponent, {
      width: '800px',
      maxHeight: '600px',
      minHeight: '600px',
      data: { mailAccountId: this.email.mailAccountId },
      autoFocus: false,
      panelClass: 'email-contacts-dialog-panel',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.contactCtrl.enable();
      if (result && !isCc) {
        this.toList = this.toList.concat(result);
        const notValidEmails: string[] = [];
        this.toList.forEach((e, i, arr) =>
          arr.indexOf(e) === i ? null : notValidEmails.push(e)
        );
        this.toList = this.toList.filter((e, i, arr) => arr.indexOf(e) === i);
        this.initToList = this.toList;
        if (notValidEmails.length) {
          this.openErrorDialog(
            'These emails already have been added',
            `${notValidEmails.join('; ')}`
          );
        }
      }
      if (result && isCc) {
        this.ccList = this.ccList.concat(result);
        const notValidEmails: string[] = [];
        this.ccList.forEach((e, i, arr) =>
          arr.indexOf(e) === i ? null : notValidEmails.push(e)
        );
        this.ccList = this.ccList.filter((e, i, arr) => arr.indexOf(e) === i);
        this.initCcList = this.ccList;
        if (notValidEmails.length) {
          this.openErrorDialog(
            'These emails already have been added',
            `${notValidEmails.join('; ')}`
          );
        }
      }
    });
  }

  private async createDraft(): Promise<EmailContract> {
    const createRequest = {
      from: null,
      to: null,
      cc: null,
      subject: null,
      body: null,
      attachmentFiles: new Array<Attachment>(),
      bodyContentType: EmailBodyContentType.Html,
      mailAccountId: this.mailAccountId,
      tags: [],
      notes: [],
      splitSend: false,
    };

    const response = await this.emailClient.createDraft(createRequest);
    return response.email;
  }

  public templateSelected(value: TemplateGetByIdContract): void {
    this.templateSubject = value.subject;
    this.templateBody = sanitizeEmailBodyHtml(value.body ?? '');
    if (this.form.value.subject || this.body) {
      this.openConfirmDialog('bodyAndSubjectChangeConfirmation');
    } else {
      this.changeData();
    }
  }

  public changeData(): void {
    if (this.body?.length && this.body.slice(0, 8) === '<br />--') {
      this.templateBody = this.templateBody + this.body;
    } else {
      if (this.templateSubject) {
        this.form?.get('subject')?.setValue(this.templateSubject);
      } else {
        this.form?.get('subject')?.setValue('');
      }
    }

    this.body = this.templateBody;
    this.templateSubject = '';
    this.templateBody = '';
    if (this.useMobileView) {
      this.menuIsOpened = false;
    }
  }

  async audit(): Promise<void> {
    this.menuContent = EmailMenuContent.Audit;
    this.menuIsOpened = true;
  }

  private async getEmail(emailId: number): Promise<EmailContract> {
    const emailResponse = await this.emailClient.getById(
      new BaseGetByIdRequest(emailId)
    );
    return emailResponse.email;
  }

  public async loadData(): Promise<void> {
    this.isLoading = true;
    this.cannotLoadData = false;
    try {
      this.email = this.emailId
        ? await this.getEmail(this.emailId)
        : await this.createDraft();
      this.emailId = this.email.emailId;
      const contactsResponse = await this.contactClient.searchForAccount(
        this.contactListRequest(null)
      );

      this.contactsListTo = contactsResponse.data.map(
        c => c.emails.find(el => el.isDefault)?.email ?? c.emails[0].email
      );
      this.contactsListCc = contactsResponse.data.map(
        c => c.emails.find(el => el.isDefault)?.email ?? c.emails[0].email
      );
      this.initToContacts = this.contactsListTo;
      this.initCcContacts = this.contactsListCc;
      const emailTagsResponse = await this.emailTagClient.getEmailTags(
        new BaseGetByIdRequest(this.email.emailId ?? null)
      );

      this.thisTags = emailTagsResponse.data;
      this.newTags = this.thisTags;
      this.email.tags = emailTagsResponse.data;

      if (this.email.to) {
        this.toList = this.email.to.split(';');
        this.initToList = this.email.to.split(';');
      } else if (this.initialToRecipients?.length) {
        this.toList = [...new Set(this.initialToRecipients)];
        this.initToList = [...this.toList];
      }
      if (this.email.cc) {
        this.ccList = this.email.cc.split(';');
        this.initCcList = this.email.cc.split(';');
      }

      this.email.from && (this.senderAddress = this.email.from);
      const draftAttachments = this.email.attachmentFiles ?? [];
      if (this.email.body) {
        const prepared = await this.prepareBody(
          this.email.body,
          draftAttachments
        );
        this.body = prepared.body;
        this.attachmentFiles = excludeInlineAttachments(
          draftAttachments.map(a => ({
            progress: 100,
            attachmentId: a.attachmentId,
            fileId: a.fileId,
            fileName: a.fileName,
            mimeType: a.mimeType,
            size: a.size,
            fileState: a.fileState,
          })),
          prepared.inlineAttachmentFileIds
        );
      } else {
        this.body = '';
        this.attachmentFiles = draftAttachments.map(a => ({
          progress: 100,
          attachmentId: a.attachmentId,
          fileId: a.fileId,
          fileName: a.fileName,
          mimeType: a.mimeType,
          size: a.size,
          fileState: a.fileState,
        }));
      }
      this.totalSize = !this.email.totalSize ? new Blob([this.body]).size : 0;
    } catch (e) {
      console.log(e);
      this.matchError.logError(e);
      this.cannotLoadData = true;
    } finally {
      this.isLoading = false;
    }
  }

  calculateDraftHash(): any {
    const obj = {
      to: this.toList,
      cc: this.ccList,
      subject: this.form.get('subject')?.value,
      body: this.body || '',
      tags: this.thisTags,
      attachments: this.attachmentFiles.map(f => f.fileId),
    };

    return objectHash(obj);
  }

  async onClickReset(): Promise<void> {
    this.attachmentFiles = [];
    this.toList = [];
    this.ccList = [];
    this.newTags = [];
    this.thisTags = [];
    this.form.get('subject')?.setValue('');
    this.body = '';
    this.emailId &&
      (await this.emailTagClient.saveTags({
        emailsId: [this.emailId],
        tags: this.thisTags.map(el => el.tag),
      }));
    await this.updateDraft(true);
  }

  public openConfirmDialog(name: string, title?: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: { name, isDontNeedConfirmWord: true, title },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (name === 'resetSubtitle') {
          this.onClickReset();
        } else {
          this.changeData();
        }
      }
    });
  }

  async updateDraft(autoSaving: boolean, setStateDraft = false): Promise<void> {
    try {
      if (
        (this.submitInProgress && autoSaving) ||
        this.isLoading ||
        !this.email
      ) {
        return;
      }
      if (autoSaving) {
        console.info(
          '%c Email autosaving... ',
          'background: #000000; color: #00bfff'
        );
      }
      const updateRequest: EmailUpdateRequest = {
        attachmentFiles: new Array<Attachment>(),
        from: this.senderAddress,
        to: this.toList.join(';'),
        cc: this.ccList.join(';'),
        subject: this.form.controls.subject.value,
        body: this.body,
        bodyContentType: EmailBodyContentType.Html,
        emailId: this.email?.emailId,
        tags: this.newTags.map(el => el.tag),
      };

      if (
        !updateRequest.to &&
        !updateRequest.cc &&
        !updateRequest.attachmentFiles.length &&
        !updateRequest.subject &&
        !updateRequest.body &&
        !updateRequest.tags.length &&
        !setStateDraft
      ) {
        if (this.email?.state === EmailState.Draft) {
          updateRequest.state = EmailState.Draft;
        } else {
          if (this.email?.state !== EmailState.VirusDetected) {
            updateRequest.state = EmailState.PreDraft;
          } else {
            updateRequest.state = EmailState.Draft;
          }
        }
      } else {
        updateRequest.state = EmailState.Draft;
      }
      this.email.state = updateRequest.state;
      updateRequest.splitSend = this.isSplitEmail;
      const attachments = this.attachmentFiles;
      attachments
        .filter(e => e?.fileId)
        .forEach(file => {
          const emailAttachment = {
            fileId: file.fileId,
            fileName: file.fileName,
            mimeType: file.mimeType,
            size: file.size,
          };
          updateRequest.attachmentFiles.unshift(emailAttachment);
        });
      await this.emailClient.update(updateRequest);
      this.previousHash = this.calculateDraftHash();
      const emailId = this.emailId;
      const isNotesChanged = this.isNotesChanged;
      this.changeDraftData.emit({ emailId, updateRequest, isNotesChanged });
      if (autoSaving) {
        console.info(
          '%c Success autosaving ',
          'background: #000000; color: #00ffbf'
        );
      }
    } catch (error: any) {
      if (autoSaving) {
        this.snackbarService.openSnackBar('errorFailedAutosaving');
        this.matchError.logError(error);
        console.info(
          '%c Failed autosaving ',
          'background: #000000; color: #ff4000'
        );
      } else {
        throw error;
      }
    }
  }

  public hasError = (controlName: string, errorName: string) => {
    return this.form?.controls?.[controlName]?.hasError(errorName);
  };

  get validateForm(): boolean {
    if (!this.form) {
      return false;
    }
    return this.form.valid && this.validateTo() && this.validateCc();
  }

  private validateTo(): boolean {
    if (this.toList.length === 0) {
      return false;
    }

    for (const to of this.toList) {
      if (!this.validateEmail(to)) {
        return false;
      }
    }
    return true;
  }

  validateEmail(email: string): boolean {
    return !!String(email).toLowerCase().match(EMAIL_REGEX);
  }

  private validateCc(): boolean {
    for (const cc of this.ccList) {
      if (!this.validateEmail(cc)) {
        return false;
      }
    }
    return true;
  }

  public get isEveryFileUploaded(): boolean {
    return this.attachmentFiles.every(e => e?.fileId);
  }

  addAttachmentFile(files: File[]): void {
    for (const selectedFile of files) {
      if (!selectedFile.name) return;
      const uploadFile = {
        fileName: selectedFile.name,
        size: selectedFile.size,
        mimeType: selectedFile.type,
        progress: 0,
        fileState: this.attachmentState.CheckInProgress,
      };

      if (!uploadFile.mimeType) {
        uploadFile.mimeType = 'application/octet-stream';
      }

      if (this.attachmentFiles.length > 0) {
        const existAttachment = this.attachmentFiles.filter(
          x => x.fileName === selectedFile.name
        );
        if (existAttachment.length === 0) {
          const newAttachments = this.attachmentFiles.filter(
            x => x.fileName !== selectedFile.name
          );
          newAttachments.unshift(uploadFile);
          this.attachmentFiles = newAttachments;
        }
      } else {
        const newAttachments = this.attachmentFiles.filter(
          x => x.fileName !== selectedFile.name
        );
        newAttachments.unshift(uploadFile);
        this.attachmentFiles = newAttachments;
      }

      const notUploadFiles = this.attachmentFiles.filter(
        x => x.fileName === selectedFile.name && x.progress === 0
      );
      if (notUploadFiles.length !== 0) {
        const reader = new FileReader();
        reader.onload = () => {
          const content = (reader.result as string).split(',')[1];
          this.uploadFile(selectedFile.name, uploadFile.mimeType, content);
        };

        reader.readAsDataURL(selectedFile);
      }
    }
  }

  addAttachmentFileFromButton(event: any): void {
    this.addAttachmentFile(event.target.files);
    this.uploader.nativeElement.value = '';
  }

  uploadFile(fileName: string, mimeType: string, content: string): void {
    if (!this.emailId) return;
    const subscription = this.fileClient
      .upload({
        fileName,
        mimeType,
        content,
        emailId: this.emailId,
      })
      .subscribe(
        event => {
          if (event.type === HttpEventType.UploadProgress) {
            this.attachmentFiles.forEach(attachment => {
              if (attachment.fileName === fileName) {
                attachment.progress = Math.round(
                  (100 * event.loaded) / event.total
                );
              }
            });
          } else if (event instanceof HttpResponse) {
            if (event.status === 200) {
              // this.fileId = event.body.fileId;
              this.attachmentFiles.forEach(attachment => {
                if (attachment.fileName === fileName) {
                  attachment.fileId = event.body.fileId;
                }
              });
              if (this.isNeedToUpdate) {
                this.updateDraft(false);
              }
            } else {
              if (this.isNeedToUpdate) {
                this.attachmentFiles = this.attachmentFiles.filter(
                  item => item.fileName !== fileName
                );
                this.openErrorDialog(
                  'error',
                  `An error occurred while uploading the file: ${fileName}.`
                );
              }
            }
          }
        },
        error => {
          if (this.isNeedToUpdate) {
            this.openErrorDialog(
              'error',
              this.pipeErrors.transform(
                error.error?.code ?? error.error?.Code ?? ''
              ) || `An error occurred while uploading the file: ${fileName}.`,
              fileName
            );
            // this.snackbarHelper.openSnackBar(`An error occurred while downloading the file: ${fileName}.`);
            this.attachmentFiles = this.attachmentFiles.filter(
              item => item.fileName !== fileName
            );
          }
        }
      );

    const cancelRequestIfCloseComponent = setInterval(() => {
      if (!this.isNeedToUpdate) {
        subscription?.unsubscribe();
        clearInterval(cancelRequestIfCloseComponent);
      }
    }, 400);
  }

  async deleteAttachment(file: AttachmentUploadContract): Promise<void> {
    const index = this.attachmentFiles.indexOf(file);
    this.attachmentFiles.splice(index, 1);
  }

  public showNotes(): void {
    this.menuContent = EmailMenuContent.Notes;
    this.menuIsOpened = true;
  }

  public showTemplates(): void {
    this.menuContent = EmailMenuContent.Templates;
    this.menuIsOpened = true;
  }

  public refreshNotes(): void {
    this.isNotesChanged = true;
    this.refreshNotesDraft.emit();
  }

  public closeMenu(): void {
    this.menuIsOpened = false;
    this.menuContent = null;
  }

  async onSubmit(send: boolean): Promise<void> {
    if (send && !this.validateForm) {
      return;
    }

    this.submitInProgress = true;
    this.cannotSaveData = false;
    this.drawerService.disabledDrawer(true);

    try {
      await this.updateDraft(false, true);
      if (send) {
        if (this.canSend) {
          await this.emailClient.markForSend(
            new BaseGetByIdRequest(this.email.emailId ?? null)
          );
        } else {
          await this.emailClient.readyToValidate(
            new BaseGetByIdRequest(this.email.emailId ?? null)
          );
        }
        this.isSaveOnClose = false;
        this.deleteFromData.emit(this.email.emailId);
      }
      await this.userWorkspaceService.refreshUnread({
        mailAccountId: this.email.mailAccountId,
      });
      this.submitted.emit();
      this.drawerService.disabledDrawer(false);
      this.drawerService.closeDrawer();
    } catch (e) {
      this.cannotSaveData = true;
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
      this.drawerService.disabledDrawer(false);
    } finally {
      this.submitInProgress = false;
    }
  }

  async delete(): Promise<any> {
    try {
      if (!this.emailId) return;
      await this.emailClient.delete({ emailIds: [this.emailId] });
      this.submitted.emit();
      this.isSaveOnClose = false;
      this.drawerService.closeDrawer();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async saveAsDraft(): Promise<any> {
    this.saveInProgress = true;
    try {
      await this.onSubmit(false);
    } catch (e) {
      this.matchError.logError(e);
    } finally {
      this.isSaveOnClose = false;
      this.saveInProgress = false;
    }
  }

  public changeToList(value: string[]): void {
    this.toList = value;
  }

  public changeCcList(value: string[]): void {
    this.ccList = value;
  }

  openAddTag(): void {
    this.menuContent = EmailMenuContent.Tags;
    this.menuIsOpened = true;
  }

  async createTag(newTag: ChangedTags): Promise<void> {
    this.thisTags = newTag.tags;
    this.newTags = this.thisTags;
    this.email = {
      ...this.email,
      tags: this.thisTags,
    };
    this.menuContent = null;
    this.menuIsOpened = false;
  }

  private async prepareBody(rawBody: string, draftAttachments: Attachment[]) {
    if (!this.emailId) {
      return {
        body: sanitizeEmailBodyHtml(rawBody),
        objectUrls: [],
        inlineAttachmentFileIds: [],
      };
    }

    const fileEmailIdByFileId = new Map<number, number>();
    draftAttachments.forEach(attachment => {
      if (attachment.fileId) {
        fileEmailIdByFileId.set(attachment.fileId, this.emailId!);
      }
    });

    let attachmentsForCid = draftAttachments;
    const hasCidReference = /src\s*=\s*(["']?)cid:/i.test(rawBody);
    const sourceEmailId =
      this.email?.parentEmailId ?? this.inlineImagesSourceEmailId;

    // Reply drafts keep cid in body but do not copy parent attachments.
    if (hasCidReference && sourceEmailId && sourceEmailId !== this.emailId) {
      try {
        const sourceEmail = await this.getEmail(sourceEmailId);
        const sourceAttachments = sourceEmail.attachmentFiles ?? [];
        sourceAttachments.forEach(attachment => {
          if (attachment.fileId) {
            fileEmailIdByFileId.set(attachment.fileId, sourceEmailId);
          }
        });
        attachmentsForCid = [...draftAttachments, ...sourceAttachments];
      } catch (e) {
        this.matchError.logError(e);
      }
    }

    return prepareEmailBodyHtml(
      rawBody,
      attachmentsForCid,
      async attachment => {
        if (!attachment.fileId) {
          return undefined;
        }

        const imageEmailId =
          fileEmailIdByFileId.get(attachment.fileId) ?? this.emailId!;

        const response = await this.fileClient.getContent(
          new BaseGetByEmailFileIdRequest(attachment.fileId, imageEmailId)
        );

        if (!response?.file) {
          return undefined;
        }

        return {
          file: response.file,
          type: response.type || attachment.mimeType,
        };
      }
    );
  }

  onDragOver($event: any): void {
    $event.preventDefault();
    this.isDragging = true;
  }
}
